import { useState } from "react";
import { IconArrowLeft, IconArrowRight, IconPlusMedium } from "central-icons-outline";

import { TemplateIcon } from "@/components/contract-intelligence/templates/TemplateIcon";
import { Button, Tabs } from "@/components/ui";
import { CONTRACT_TEMPLATES, TEMPLATE_CATEGORIES } from "@/mockData/contractTemplates";
import type { ContractTemplate, ContractTemplateCategoryId } from "@/types/contractIntelligence.types";

interface ContractTemplatePickerProps {
  onPick: (template: ContractTemplate) => void;
  onCancel: () => void;
  onUploadExisting: () => void;
}

export function ContractTemplatePicker({ onPick, onCancel, onUploadExisting }: ContractTemplatePickerProps) {
  const [category, setCategory] = useState<ContractTemplateCategoryId>("all");

  const filtered =
    category === "all" ? CONTRACT_TEMPLATES : CONTRACT_TEMPLATES.filter((t) => t.category === category);

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <header className="shrink-0 border-b border-border px-6 py-4 md:px-7">
        <div className="flex items-start gap-3">
          <Button type="button" variant="outline" size="icon" className="size-8 shrink-0" onClick={onCancel} aria-label="Back">
            <IconArrowLeft size={16} ariaHidden />
          </Button>
          <div className="min-w-0 flex-1">
            <p className="flex items-center gap-2">
              Template Library
            </p>
            <h1 className="mt-1 text-lg font-medium">
              Generate a contract from a template
            </h1>
            <p className="mt-1 text-xs text-muted-foreground md:text-sm">
              Pick a template, answer a few questions, and Ari fills the draft. Then redline it like any other contract.
            </p>
          </div>
        </div>
      </header>

      <Tabs value={category} onValueChange={(v) => setCategory(v as ContractTemplateCategoryId)} className="flex min-h-0 flex-1 flex-col">
        <Tabs.List variant="underline" className="w-full shrink-0 justify-start gap-0 overflow-x-auto border-b border-border px-6 md:px-7">
          {TEMPLATE_CATEGORIES.map((cat) => (
            <Tabs.Trigger key={cat.id} value={cat.id} className="gap-2 px-3 text-xs">
              {cat.label}
              <span className="font-mono text-[10px] text-muted-foreground">{cat.count}</span>
            </Tabs.Trigger>
          ))}
        </Tabs.List>

        <Tabs.Panel value={category} className="min-h-0 flex-1 overflow-y-auto">
            <div className="grid gap-3 p-5 pb-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 md:px-7 md:py-6">
              {filtered.map((template) => (
                  <div
                    key={template.id}
                    onClick={() => onPick(template)}
                    className="relative flex min-h-[236px] cursor-pointer flex-col overflow-hidden rounded-3xl border border-border bg-card p-4 text-left"
                    style={{ "--tpl-color": template.color } as React.CSSProperties}
                  >
                    <div className="flex items-start gap-3">
                      <span
                        className="flex size-9 shrink-0 items-center justify-center rounded-lg"
                        style={{
                          backgroundColor: "color-mix(in srgb, var(--tpl-color) 12%, transparent)",
                          color: "var(--tpl-color)",
                        }}
                      >
                        <TemplateIcon name={template.icon} className="size-[18px]" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-[13px] font-semibold leading-snug">{template.name}</p>
                        <div className="mt-1 flex items-center gap-2">
                          <p className="font-mono text-[10px] tracking-wide text-muted-foreground uppercase">
                            {template.pages}p · {template.sections.length} sections
                          </p>
                          {template.use ? (
                            <span
                              className="rounded-full px-1.5 py-px text-[9px] font-semibold tracking-wider uppercase"
                              style={{
                                backgroundColor: "color-mix(in srgb, var(--tpl-color) 14%, transparent)",
                                color: "var(--tpl-color)",
                              }}
                            >
                              {template.use}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>

                    <p className="mt-3 text-[11.5px] leading-relaxed text-muted-foreground">{template.subtitle}</p>

                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {template.sections.slice(0, 4).map((section) => (
                        <span
                          key={section}
                          className="rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"
                        >
                          {section}
                        </span>
                      ))}
                      {template.sections.length > 4 ? (
                        <span className="self-center text-[10px] font-medium text-muted-foreground/70">
                          +{template.sections.length - 4}
                        </span>
                      ) : null}
                    </div>

                    <div className="flex-1" />

                    {template.examples?.[0] ? (
                      <p className="mt-3 flex items-center gap-1.5 truncate border-t border-border/70 pt-3 text-[10.5px] text-muted-foreground">
                        <span
                          aria-hidden="true"
                          className="size-1.5 shrink-0 rounded-full bg-(--tpl-color)"
                        />
                        <span className="truncate">
                          <span className="text-muted-foreground/70">Used for </span>
                          <span className="font-medium text-foreground/85">{template.examples[0]}</span>
                        </span>
                      </p>
                    ) : null}

                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-3 h-9 w-full justify-center font-medium"
                      onClick={(e) => {
                        e.stopPropagation();
                        onPick(template);
                      }}
                    >
                      Use template
                      <IconArrowRight size={14} ariaHidden />
                    </Button>
                  </div>
              ))}

              <button
                type="button"
                onClick={onUploadExisting}
                className="flex min-h-[236px] flex-col items-center justify-center gap-2.5 rounded-2xl border border-dashed border-border bg-transparent p-4 text-muted-foreground"
              >
                <span className="flex size-10 items-center justify-center rounded-full border border-dashed border-border text-muted-foreground">
                  <IconPlusMedium size={20} ariaHidden />
                </span>
                <span className="text-xs font-medium text-foreground/80">Upload existing contract</span>
                <span className="text-[11px]">Have a draft already? Drop it in.</span>
              </button>
            </div>
        </Tabs.Panel>
      </Tabs>
    </div>
  );
}
